import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "professional" ? "professional" : "client";
  const [tab, setTab] = useState(mode);
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", profession_title: "", bio: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (tab === "login") {
        const u = await login(form.email, form.password);
        toast.success("Welcome back, " + u.name);
        nav("/dashboard");
      } else {
        const u = await register({ ...form, role });
        toast.success("Welcome to Aloka, " + u.name);
        nav("/dashboard");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed");
    } finally { setLoading(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16" data-testid="auth-page">
      <h1 className="font-display text-4xl font-black tracking-tight text-slate-900 mb-2">
        {tab === "login" ? "Welcome back" : "Create your account"}
      </h1>
      <p className="text-sm text-slate-500 mb-8">
        {tab === "login" ? "Login to access your dashboard." : "Pick a role to get started."}
      </p>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger value="login" data-testid="tab-login">Login</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
        </TabsList>
        <TabsContent value="register" className="pt-4">
          <div className="grid grid-cols-2 gap-2 mb-4">
            <Button type="button" variant={role === "client" ? "default" : "outline"} onClick={() => setRole("client")} data-testid="role-client">Client</Button>
            <Button type="button" variant={role === "professional" ? "default" : "outline"} onClick={() => setRole("professional")} data-testid="role-professional">CA / Professional</Button>
          </div>
        </TabsContent>
      </Tabs>

      <form onSubmit={onSubmit} className="space-y-4">
        {tab === "register" && (
          <>
            <div><Label>Full Name</Label><Input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="input-name" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="input-phone" /></div>
            {role === "professional" && (<>
              <div><Label>Profession (CA / CS / Lawyer / CFP)</Label><Input value={form.profession_title} onChange={e => setForm({...form, profession_title: e.target.value})} data-testid="input-profession" /></div>
              <div><Label>Short Bio</Label><Input value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} data-testid="input-bio" /></div>
            </>)}
          </>
        )}
        <div><Label>Email</Label><Input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="input-email" /></div>
        <div><Label>Password</Label><Input type="password" required value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="input-password" /></div>
        <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="auth-submit-btn">
          {loading ? "Please wait…" : (tab === "login" ? "Login" : "Create account")}
        </Button>
      </form>

      <div className="my-5 text-center text-xs text-slate-500">— OR —</div>
      <Button variant="outline" className="w-full" onClick={googleSignIn} data-testid="google-login-btn">
        Continue with Google
      </Button>
      <p className="text-xs text-slate-500 mt-6 text-center">
        {tab === "login" ? <>No account? <button className="underline" onClick={() => setTab("register")} data-testid="switch-to-register">Register</button></>
                        : <>Already have an account? <button className="underline" onClick={() => setTab("login")} data-testid="switch-to-login">Login</button></>}
      </p>
      <p className="text-[11px] text-slate-400 text-center mt-4">
        By continuing you agree to our <Link to="/about" className="underline">Terms</Link>.
      </p>
    </div>
  );
}
